import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, IconButton, Tooltip, Chip, Alert, Snackbar,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/VpnKey';
import type { Api, TenantView, ApiTokenView } from './api';
import { ConfirmDialog } from './dialogs';

const fmt = (s: string | null) => (s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

// API tokens for programmatic docs writes (developers / Claude). The plaintext is
// shown exactly once, at creation. Admin-only.
export function ApiTokens({ api, tenant }: { api: Api; tenant: TenantView }) {
  const [tokens, setTokens] = useState<ApiTokenView[] | null>(null);
  const [err, setErr] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiresDays, setExpiresDays] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiTokenView | null>(null);
  const [toast, setToast] = useState('');

  const load = () => api.apiTokens(tenant.tenantId).then(setTokens).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, [tenant.tenantId]);

  const create = async () => {
    try {
      const expiresAt = expiresDays ? new Date(Date.now() + Number(expiresDays) * 86400_000).toISOString() : null;
      const { token } = await api.createApiToken(tenant.tenantId, { name: name.trim() || 'API token', scopes: ['docs:write'], expiresAt });
      setCreateOpen(false); setName(''); setExpiresDays('');
      setNewToken(token);
      load();
    } catch (e: any) { setErr(e.message); }
  };
  const revoke = async (t: ApiTokenView) => {
    try { await api.revokeApiToken(tenant.tenantId, t.id); load(); } catch (e: any) { setErr(e.message); }
  };
  const copy = (s: string) => { navigator.clipboard?.writeText(s).then(() => setToast('Copied')).catch(() => {}); };

  if (!tokens) return <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      {err ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert> : null}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><KeyIcon fontSize="small" /> API Tokens</Typography>
          <Typography variant="body2" color="text.secondary">Tokens let developers (or Claude) write docs via the API. Every API write lands as a draft in review — it can never publish directly.</Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>New token</Button>
      </Box>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell><TableCell>Token</TableCell><TableCell>Scopes</TableCell>
              <TableCell>Last used</TableCell><TableCell>Expires</TableCell><TableCell>Status</TableCell><TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.length === 0 ? (
              <TableRow><TableCell colSpan={7} sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>No tokens yet.</TableCell></TableRow>
            ) : tokens.map((t) => {
              const revoked = !!t.revokedAt;
              const expired = t.expiresAt && new Date(t.expiresAt).getTime() < Date.now();
              return (
                <TableRow key={t.id} sx={{ opacity: revoked ? 0.5 : 1 }}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{t.prefix}…</TableCell>
                  <TableCell>{t.scopes.map((s) => <Chip key={s} label={s} size="small" sx={{ mr: 0.5 }} />)}</TableCell>
                  <TableCell>{fmt(t.lastUsedAt)}</TableCell>
                  <TableCell>{fmt(t.expiresAt)}</TableCell>
                  <TableCell>{revoked ? <Chip label="Revoked" size="small" color="default" /> : expired ? <Chip label="Expired" size="small" color="warning" /> : <Chip label="Active" size="small" color="success" />}</TableCell>
                  <TableCell align="right">
                    {!revoked ? <Tooltip title="Revoke"><IconButton size="small" onClick={() => setConfirmRevoke(t)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip> : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New API token</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label="Name" placeholder="e.g. release-5.0 docs bot" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField fullWidth margin="dense" label="Expires in (days, optional)" type="number" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} />
          <Typography variant="caption" color="text.secondary">Scope: <b>docs:write</b> (read + propose draft docs).</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={create}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Show plaintext once */}
      <Dialog open={!!newToken} onClose={() => setNewToken(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy your token now</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>This is the only time the full token is shown. Store it securely — you can’t see it again.</DialogContentText>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField fullWidth value={newToken ?? ''} InputProps={{ readOnly: true, sx: { fontFamily: 'ui-monospace, monospace', fontSize: 13 } }} />
            <Tooltip title="Copy"><IconButton onClick={() => newToken && copy(newToken)}><ContentCopyIcon /></IconButton></Tooltip>
          </Box>
        </DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setNewToken(null)}>Done</Button></DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmRevoke} title="Revoke token?"
        message={`"${confirmRevoke?.name}" will stop working immediately. This can’t be undone.`}
        confirmLabel="Revoke" danger
        onClose={(ok) => { if (ok && confirmRevoke) revoke(confirmRevoke); setConfirmRevoke(null); }}
      />
      <Snackbar open={!!toast} autoHideDuration={1500} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}
